import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ExternalLink, MessageCircle, ShoppingBag } from 'lucide-react';
import { ordersService, Order, OrderStatus } from '@/services/orders';
import { formatPrice, toWhatsAppPhone } from '@/utils/format';
import { Tenant } from '@/types';

const STATUS_LABEL: Record<OrderStatus, string> = {
  pending: 'Pendiente', confirmed: 'Confirmado', preparing: 'En preparación',
  shipped: 'Enviado', delivered: 'Entregado', cancelled: 'Cancelado',
};
const STATUS_COLOR: Record<OrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-800', confirmed: 'bg-blue-100 text-blue-800',
  preparing: 'bg-indigo-100 text-indigo-800', shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-rose-100 text-rose-800',
};
const ALL: OrderStatus[] = ['pending', 'confirmed', 'preparing', 'shipped', 'delivered', 'cancelled'];

export default function StoreOrdersWidget({ tenant }: { tenant: Tenant | null }) {
  const queryClient = useQueryClient();
  const { data: orders = [] } = useQuery({
    queryKey: ['orders', ''],
    queryFn: () => ordersService.list(),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: OrderStatus }) => ordersService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Estado actualizado');
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'No se pudo cambiar el estado'),
  });

  if (!tenant?.online_store_enabled) return null;

  const pending = orders.filter((o) => o.status === 'pending').length;
  const recent = orders.slice(0, 5);
  const storeUrl = `/tienda/${tenant.slug}`;

  const whatsapp = (o: Order) => {
    const phone = toWhatsAppPhone(o.customer_phone);
    const msg = `¡Hola ${o.customer_name}! Te escribo por tu pedido #${o.order_number} (${STATUS_LABEL[o.status]}). Total: ${formatPrice(o.total)}.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="bg-white rounded-2xl border border-cream-200 p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <ShoppingBag className="h-5 w-5 text-emerald-600" />
          <h3 className="font-semibold text-taupe-800">Tienda online</h3>
          {pending > 0 && (
            <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {pending} pendiente{pending > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <a
            href={storeUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
          >
            <ExternalLink className="h-4 w-4" /> Ver tienda
          </a>
          <Link to="/dashboard/orders" className="text-sm text-taupe-600 hover:text-taupe-800 font-medium">
            Ver todos
          </Link>
        </div>
      </div>

      {recent.length === 0 ? (
        <p className="text-sm text-taupe-400 py-4 text-center">Todavía no hay pedidos.</p>
      ) : (
        <div className="divide-y divide-cream-100">
          {recent.map((o) => (
            <div key={o.id} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-taupe-700">#{o.order_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                </div>
                <p className="text-sm text-taupe-700 truncate">{o.customer_name} · {formatPrice(o.total)}</p>
                <p className="text-xs text-taupe-400 truncate">
                  {o.delivery_method === 'pickup' ? 'Retiro' : 'Envío'}
                  {' · '}
                  <span className="capitalize">{o.payment_method}</span>
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => whatsapp(o)} title="WhatsApp al cliente" className="p-1.5 rounded-lg bg-[#25D366]/10 text-[#1da851] hover:bg-[#25D366]/20">
                  <MessageCircle className="h-4 w-4" />
                </button>
                <select
                  value={o.status}
                  onChange={(e) => statusMutation.mutate({ id: o.id, status: e.target.value as OrderStatus })}
                  disabled={statusMutation.isPending}
                  className="px-2 py-1.5 border border-cream-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-taupe-400"
                >
                  {ALL.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
